import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { trainingReminders } from '../../services/api';
import { isIos, isStandalone } from '../../utils/pwaInstall';
import { requestNotificationPermission, subscribeDevice, unsubscribeDevice } from '../../utils/pushReminders';
import './TrainingReminderSettings.css';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

/**
 * Lembretes de treino do atleta: ligar/desligar, 30 ou 60 minutos antes, e a hora a que costuma
 * treinar.
 *
 *  - A permissão de notificações pede-se AQUI (quando o atleta liga os lembretes), nunca à entrada da app.
 *  - iPhone: o Web Push exige a app instalada (16.4+). Se não estiver, diz-se isso em vez de falhar em silêncio.
 *  - Sem chaves VAPID no servidor, o interruptor diz que ainda não está disponível (não finge que ligou).
 */
export default function TrainingReminderSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [minutesBefore, setMinutesBefore] = useState(60);
  const [hour, setHour] = useState('');
  const [pushConfigured, setPushConfigured] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState('');
  const [standalone, setStandalone] = useState(true);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    setStandalone(isStandalone());
    setIos(isIos());
    (async () => {
      try {
        const { data } = await trainingReminders.settings();
        setEnabled(!!data.enabled);
        setMinutesBefore(data.minutesBefore === 30 ? 30 : 60);
        setHour(data.preferredHour === null || data.preferredHour === undefined ? '' : String(data.preferredHour));
        setPushConfigured(!!data.pushConfigured);
        setVapidPublicKey(data.vapidPublicKey || '');
      } catch (e) {
        toast.error('Não foi possível carregar os teus lembretes.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await trainingReminders.updateSettings(patch);
    } catch (e) {
      toast.error('Não foi possível guardar.');
    } finally {
      setSaving(false);
    }
  };

  // iPhone sem a app instalada: o Web Push não funciona. Explica-se em vez de falhar em silêncio.
  const iosNeedsInstall = ios && !standalone;

  const toggle = async () => {
    if (enabled) {
      try {
        const endpoint = await unsubscribeDevice();
        if (endpoint) await trainingReminders.unsubscribe(endpoint);
      } catch (e) {
        // mesmo sem subscrição local, desligar as definições é o que o atleta pediu
      }
      setEnabled(false);
      await save({ enabled: false, minutesBefore, preferredHour: hour === '' ? null : Number(hour) });
      return;
    }

    if (!pushConfigured || !vapidPublicKey) {
      toast.info('Os lembretes ainda não estão disponíveis neste ginásio.');
      return;
    }
    if (iosNeedsInstall) {
      toast.info('No iPhone, instala primeiro a app no ecrã principal para receberes notificações.');
      return;
    }

    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      toast.info('Precisas de permitir as notificações para receberes os lembretes.');
      return;
    }

    try {
      const subscription = await subscribeDevice(vapidPublicKey);
      await trainingReminders.subscribe(subscription);
      setEnabled(true);
      await save({ enabled: true, minutesBefore, preferredHour: hour === '' ? null : Number(hour) });
      toast.success('Lembretes de treino ligados.');
    } catch (e) {
      toast.error('Não foi possível subscrever as notificações neste dispositivo.');
    }
  };

  const changeMinutes = async (value) => {
    setMinutesBefore(value);
    await save({ enabled, minutesBefore: value, preferredHour: hour === '' ? null : Number(hour) });
  };

  const changeHour = async (value) => {
    setHour(value);
    await save({ enabled, minutesBefore, preferredHour: value === '' ? null : Number(value) });
  };

  if (loading) return null;

  return (
    <div className="profile-card reminder-card">
      <h3>🔔 Lembretes de treino</h3>
      <p className="reminder-card__hint">
        Recebe uma notificação antes do teu treino, com uma mensagem para te pôr a caminho.
      </p>

      {iosNeedsInstall && (
        <p className="reminder-card__notice" data-testid="reminder-ios-notice">
          No iPhone, adiciona o GyMag ao ecrã principal (Partilhar → Adicionar ao ecrã principal) para
          poderes receber notificações.
        </p>
      )}

      {!pushConfigured && (
        <p className="reminder-card__notice" data-testid="reminder-unavailable">
          Os lembretes por notificação ainda não estão disponíveis neste ginásio.
        </p>
      )}

      <div className="reminder-card__row">
        <label className="toggle-switch" style={{ margin: 0 }}>
          <input
            type="checkbox"
            aria-label="Receber lembretes de treino"
            checked={enabled}
            disabled={saving}
            onChange={toggle}
          />
          <span className="toggle-slider" />
        </label>
        <span className="reminder-card__state">
          {enabled ? '✅ Lembretes ligados' : 'Lembretes desligados'}
        </span>
      </div>

      {enabled && (
        <div className="reminder-card__options">
          <div className="reminder-card__field">
            <label htmlFor="reminder-minutes">Avisar-me antes</label>
            <select
              id="reminder-minutes"
              value={minutesBefore}
              disabled={saving}
              onChange={(e) => changeMinutes(Number(e.target.value))}
            >
              <option value={30}>30 minutos</option>
              <option value={60}>1 hora</option>
            </select>
          </div>

          <div className="reminder-card__field">
            <label htmlFor="reminder-hour">A que horas treinas</label>
            <select
              id="reminder-hour"
              value={hour}
              disabled={saving}
              onChange={(e) => changeHour(e.target.value)}
            >
              <option value="">—</option>
              {HOURS.map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
            <p className="reminder-card__hint">
              Usada quando não tens uma aula marcada para hoje com hora.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
